import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import ApplicationService from 'src/services/ApplicationService';
import { MarkerActionTypes } from 'src/store/reducers/marker';
import { IPlaybackState, PlaybackActionTypes } from 'src/store/reducers/playback';
import { ResolutionActionTypes } from 'src/store/reducers/resolution';
import { SubtitlesActionTypes } from 'src/store/reducers/subtitles';
import { UIActionTypes } from 'src/store/reducers/ui';
import { IPlaylist } from 'src/interfaces/Playlist';
import { isFullscreen, isPictureInPicture } from 'src/utils/browserUtils';

const useUpdateStore = () => {
  const dispatch = useDispatch();

  const updateReadyInStore = useCallback(() => {
    dispatch({ type: PlaybackActionTypes.READY });
  }, [dispatch]);

  const updateCanPlayInStore = useCallback(() => {
    dispatch({ type: PlaybackActionTypes.CAN_PLAY });
  }, [dispatch]);

  const updatePauseInStore = useCallback(
    (paused: boolean) => {
      dispatch({ type: PlaybackActionTypes.SET_PAUSED, payload: paused });
    },
    [dispatch],
  );

  const updateSeekingInStore = useCallback(
    (seeking: boolean) => {
      dispatch({ type: PlaybackActionTypes.SET_IS_SEEKING, payload: seeking });
    },
    [dispatch],
  );

  const updateVolumeInStore = useCallback(() => {
    const payload = {
      volumePercent: ApplicationService.player?.volumePercent,
      isMuted: ApplicationService.player?.isMuted,
    };
    dispatch({ type: PlaybackActionTypes.SET_VOLUME, payload });
  }, [dispatch]);

  const updateFullscreenInStore = useCallback(() => {
    dispatch({
      type: isFullscreen() ? UIActionTypes.SET_FULLSCREEN : UIActionTypes.EXIT_FULLSCREEN,
    });
  }, [dispatch]);

  const updatePlaybackRateInStore = useCallback(() => {
    dispatch({ type: PlaybackActionTypes.SET_PLAYBACK_RATE, payload: ApplicationService.player?.playbackRate });
  }, [dispatch]);

  const updatePlaylistInStore = useCallback(() => {
    dispatch({
      type: ResolutionActionTypes.SET_PLAYLIST,
      payload: ApplicationService.player?.playlists.sort((a, b) => parseInt(a.bandwidth) - parseInt(b.bandwidth)),
    });
  }, [dispatch]);

  const updateHasNativeHLSInStore = useCallback(() => {
    dispatch({
      type: PlaybackActionTypes.SET_HAS_NATIVE_HLS,
      payload: ApplicationService.player?.hasSupportToNativeHLS,
    });
  }, [dispatch]);

  const updateMarkersInStore = useCallback(() => {
    dispatch({ type: MarkerActionTypes.SET_MARKERS, payload: ApplicationService.applicationData?.markers });
  }, [dispatch]);

  const updateActiveSubtitleInStore = useCallback(() => {
    dispatch({ type: SubtitlesActionTypes.SET_ACTIVE_TRACK, payload: ApplicationService.subtitle?.activeTrack });
  }, [dispatch]);

  const updateSubtitlesInStore = useCallback(() => {
    if (!ApplicationService.subtitle?.hasSubtitles) return;

    dispatch({ type: SubtitlesActionTypes.SET_SUBTITLES, payload: ApplicationService.subtitle?.subtitlesOptions });
  }, [dispatch]);

  const updateCurrentCueTextInStore = useCallback(
    (cueText: string) => {
      dispatch({ type: SubtitlesActionTypes.SET_CURRENT_CUE_TEXT, payload: cueText });
    },
    [dispatch],
  );

  const updatePictureInPictureStatusInStore = useCallback(() => {
    dispatch({
      type: isPictureInPicture() ? UIActionTypes.SET_PICTURE_IN_PICTURE : UIActionTypes.EXIT_PICTURE_IN_PICTURE,
    });
  }, [dispatch]);

  const updateUserActiveInStore = useCallback(
    (isActive: boolean) => {
      dispatch({ type: isActive ? UIActionTypes.SET_USER_ACTIVE : UIActionTypes.UNSET_USER_ACTIVE });
    },
    [dispatch],
  );

  const updateResolutionInStore = useCallback(
    (resolution: IPlaylist) => {
      dispatch({ type: ResolutionActionTypes.SET_RESOLUTION, payload: resolution });
    },
    [dispatch],
  );

  const updateVideoPlaybackQualityInStore = useCallback(() => {
    const videoPlaybackQuality = ApplicationService.player?.videoPlaybackQuality;

    dispatch({
      type: ResolutionActionTypes.SET_VIDEO_PLAYBACK_QUALITY,
      payload: videoPlaybackQuality,
    });
  }, [dispatch]);

  const updateMediaInfo = useCallback(() => {
    const mediaInfo = ApplicationService.player?.mediaInfo;

    dispatch({
      type: ResolutionActionTypes.SET_MEDIA_INFO,
      payload: mediaInfo,
    });
  }, [dispatch]);

  const updateVideoDimensionsInStore = useCallback(
    (dimensions: IPlaybackState['videoDimensions']) => {
      dispatch({ type: PlaybackActionTypes.SET_VIDEO_DIMENSIONS, payload: dimensions });
    },
    [dispatch],
  );

  const updateBufferIndicatorInStore = useCallback(() => {
    dispatch({
      type: PlaybackActionTypes.UPDATE_BUFFER_INDICATOR_PERCENT,
      payload: ApplicationService.player?.bufferIndicatorPercent,
    });
  }, [dispatch]);

  const updateBufferedInStore = useCallback(() => {
    const buffered = ApplicationService.player?.buffered;

    const customBuffered = {
      start: buffered?.start(0),
      end: buffered?.end(buffered.length - 1),
      length: buffered?.length,
    };

    dispatch({ type: PlaybackActionTypes.SET_BUFFERED, payload: customBuffered });
  }, [dispatch]);

  return {
    updateCanPlayInStore,
    updateReadyInStore,
    updatePauseInStore,
    updateVolumeInStore,
    updateFullscreenInStore,
    updatePlaybackRateInStore,
    updatePlaylistInStore,
    updateHasNativeHLSInStore,
    updateSeekingInStore,
    updateMarkersInStore,
    updateActiveSubtitleInStore,
    updateSubtitlesInStore,
    updateCurrentCueTextInStore,
    updatePictureInPictureStatusInStore,
    updateUserActiveInStore,
    updateResolutionInStore,
    updateVideoDimensionsInStore,
    updateBufferIndicatorInStore,
    updateVideoPlaybackQualityInStore,
    updateMediaInfo,
    updateBufferedInStore,
  };
};

export default useUpdateStore;
